import classNames from 'classnames';
import { useLayoutEffect, useRef } from 'react';
import { Link, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { TestId } from '@repo/test-id';

import { EntranceCarousel } from '@features/promo';

import Logo from '@shared/components/Logo';
import { type Theme, LayoutProvider } from '@shared/contexts/LayoutContext';
import BottomBar from '@shared/components/bottomBar/BottomBar';
import HamburgerMenu from '@shared/components/HamburgerMenu';
import DesktopFooter from '@shared/components/DesktopFooter';

import { RouteName } from '@router/routeNames';
import { ROUTE_PATHS } from '@router/paths';

import bigCar from '@assets/images/parking-search/parking-car.webp';

interface Props {
  theme?: Theme;
  headerTheme?: Theme;
  bottomMenu?: boolean;
}

export default function DefaultLayout({ theme = 'soft-orange', headerTheme = 'white', bottomMenu = true }: Props) {
  const testId = TestId.root('defaultLayout');
  const location = useLocation();
  const isStartEarningPage = location.pathname === ROUTE_PATHS[RouteName.START_EARNING];

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    scrollContainerRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div
      className={classNames('flex h-full flex-col', {
        'bg-web-orange': theme === 'soft-orange' || theme === 'brand',
        'bg-white': theme === 'white',
      })}
      data-testid={testId}
    >
      <div className="hidden items-center justify-between md:flex md:shrink-0 md:p-4">
        <Link to={ROUTE_PATHS[RouteName.MAP]}>
          <Logo width={200} className="dark:text-white" />
        </Link>
        <HamburgerMenu />
      </div>
      <img src={bigCar} alt="" aria-hidden="true" className="absolute top-20 -left-60 hidden rotate-220 md:block" />

      <div className="relative flex min-h-0 w-full flex-1">
        <div ref={scrollContainerRef} className="h-full w-full overflow-auto">
          <div className={classNames('flex min-h-full', { 'xl:w-1/2': isStartEarningPage })}>
            <div className="mx-auto flex w-full max-w-sm flex-col">
              <LayoutProvider headerTheme={headerTheme}>
                <Outlet context={useOutletContext()} />
              </LayoutProvider>
            </div>
          </div>
        </div>
        {isStartEarningPage && (
          <div className="pointer-events-none absolute inset-0 hidden grid-cols-2 grid-rows-1 xl:grid">
            <div />
            <div className="flex justify-center pr-20">
              <div className="flex h-full max-h-200 min-h-0 max-w-140 justify-center pb-10">
                <div className="pointer-events-auto h-full w-full">
                  <EntranceCarousel testId={testId.append('carousel')} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <DesktopFooter />

      {bottomMenu && (
        <div className="md:hidden">
          <BottomBar />
        </div>
      )}
    </div>
  );
}
